import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  signInWithPopup,
  updateProfile,
  updatePassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import FirestoreUserService from './firestoreUserService';
import toaster from '../helpers/toaster';

class FirebaseAuthService {
  constructor() {
    this.currentUser = null;
    this.firestoreUserService = new FirestoreUserService();
    this.authInitialized = false;
    this.authPromise = null;
    this.initializeAuthListener();
  }

  // Initialize auth state listener
  initializeAuthListener() {
    this.authPromise = new Promise((resolve) => {
      onAuthStateChanged(auth, (user) => {
        this.currentUser = user;
        if (!this.authInitialized) {
          this.authInitialized = true;
          resolve(user);
        }
        
        if (user) {
          // User is signed in
          localStorage.setItem('access_token', user.accessToken || 'firebase_user');
          localStorage.setItem('user_id', user.uid);
          localStorage.setItem('user_name', user.displayName || user.email);
          localStorage.setItem('user_email', user.email);
          localStorage.setItem('user_image', user.photoURL || '');
        } else {
          // User is signed out
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_name');
          localStorage.removeItem('user_email');
          localStorage.removeItem('user_image');
        }
      });
    });
  }

  // Register with email and password
  async register(userData) {
    try {
      const { email, password, company, symplr_badge_ID, intelli_badge_ID } = userData;
      
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);

      // Update user profile with additional info
      await updateProfile(user, {
        displayName: company || email.split('@')[0]
      });

      // Create Firestore user document
      const additionalData = {
        company: company || '',
        symplr_badge_ID: symplr_badge_ID || '',
        intelli_badge_ID: intelli_badge_ID || '',
        authProvider: 'email'
      };

      const firestoreResult = await this.firestoreUserService.createUserDocument(user, additionalData);
      
      if (!firestoreResult.success) {
        console.warn('Failed to create Firestore document:', firestoreResult.message);
      }

      return {
        success: true,
        message: 'Registration successful! Please check your email (and spam folder) for a verification link.',
        user: user,
        access_token: user.accessToken || 'firebase_user',
        user_id: user.uid
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        message: { email: [this.getErrorMessage(error.code)] }
      };
    }
  }

  // Login with email and password
  async login(userData) {
    try {
      const { email, password } = userData;
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        return {
          success: false,
          message: { message: ['Please verify your email before logging in.'] }
        };
      }

      return {
        success: true,
        message: 'Login successful!',
        user: user,
        access_token: user.accessToken || 'firebase_user',
        user_id: user.uid
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: { message: [this.getErrorMessage(error.code)] }
      };
    }
  }

  // Google Sign In
  async signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Get or create Firestore user document
      const additionalData = {
        authProvider: 'google'
      };

      const firestoreResult = await this.firestoreUserService.getOrCreateUserDocument(user, additionalData);
      
      if (!firestoreResult.success) {
        console.warn('Failed to get/create Firestore document:', firestoreResult.message);
      }

      return {
        success: true,
        message: 'Google sign-in successful!',
        user: user,
        access_token: user.accessToken || 'firebase_user',
        user_id: user.uid
      };
    } catch (error) {
      console.error('Google sign-in error:', error);
      return {
        success: false,
        message: { message: [this.getErrorMessage(error.code)] }
      };
    }
  }

  // Logout
  async logout() {
    try {
      await signOut(auth);
      return {
        success: true,
        message: 'Logout successful!'
      };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        message: 'Logout failed. Please try again.'
      };
    }
  }

  // Update user password
  async updatePassword(newPassword) {
    if (!this.currentUser) {
      return { success: false, message: 'No user is currently signed in.' };
    }

    try {
      await updatePassword(this.currentUser, newPassword);
      return { success: true, message: 'Password updated successfully!' };
    } catch (error) {
      console.error('Password update error:', error);
      let message = this.getErrorMessage(error.code);
      if (error.code === 'auth/requires-recent-login') {
        message = 'This action requires you to have logged in recently. Please log out and log back in to change your password.';
      }
      return { success: false, message };
    }
  }

  // Reset password
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return {
        success: true,
        message: 'Password reset email sent!'
      };
    } catch (error) {
      console.error('Password reset error:', error);
      return {
        success: false,
        message: this.getErrorMessage(error.code)
      };
    }
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.currentUser;
  }

  // Wait for auth to be initialized
  async waitForAuthInitialization() {
    if (this.authInitialized) {
      return this.currentUser;
    }
    return await this.authPromise;
  }

  // Get user profile data (compatible with existing profile service)
  async getProfileData() {
    // Wait for auth to be initialized first
    await this.waitForAuthInitialization();
    
    if (!this.currentUser) {
      return {
        success: false,
        message: 'No user logged in'
      };
    }

    try {
      // Try to get user data from Firestore first
      const firestoreResult = await this.firestoreUserService.getUserByEmail(this.currentUser.email);
      
      if (firestoreResult.success) {
        const userData = firestoreResult.data;
        return {
          success: true,
          data: {
            id: this.currentUser.uid,
            name: userData.displayName || this.currentUser.displayName || this.currentUser.email,
            email: userData.email,
            images: userData.profileImageUrl || this.currentUser.photoURL || '',
            first_name: userData.firstName || this.currentUser.displayName?.split(' ')[0] || '',
            last_name: userData.lastName || this.currentUser.displayName?.split(' ')[1] || '',
            phone_no: userData.phoneNumber || this.currentUser.phoneNumber || '',
            company: userData.company || '',
            symplr_badge_ID: userData.symplr_badge_ID || '',
            intelli_badge_ID: userData.intelli_badge_ID || ''
          }
        };
      }
    } catch (error) {
      console.warn('Failed to get Firestore user data:', error);
    }

    // Fallback to Firebase Auth data
    return {
      success: true,
      data: {
        id: this.currentUser.uid,
        name: this.currentUser.displayName || this.currentUser.email,
        email: this.currentUser.email,
        images: this.currentUser.photoURL || '',
        first_name: this.currentUser.displayName?.split(' ')[0] || '',
        last_name: this.currentUser.displayName?.split(' ')[1] || '',
        phone_no: this.currentUser.phoneNumber || '',
        company: localStorage.getItem('company') || '',
        symplr_badge_ID: localStorage.getItem('symplr_badge_ID') || '',
        intelli_badge_ID: localStorage.getItem('intelli_badge_ID') || ''
      }
    };
  }

  // Helper method to get user-friendly error messages
  getErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No user found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters long.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/popup-closed-by-user':
        return 'Google sign-in was cancelled.';
      case 'auth/popup-blocked':
        return 'Please allow popups for Google sign-in.';
      case 'auth/requires-recent-login':
        return 'This action is sensitive and requires recent authentication. Please log in again before retrying this request.';
      default:
        return 'An error occurred. Please try again.';
    }
  }
}

export default FirebaseAuthService; 